import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ModalController, Platform } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import {
  DocumentSecurity,
  DocumentSecurityType,
} from "src/model/class/general classes/document-security";
import { AlertService } from "src/service/alert.service";
import { DocumentSecurityService } from "src/service/document-security.service";
import { LoaderService } from "src/service/loader.service";
import { SelectFieldOption } from "src/components/general components/select-field/select-field.component";
import { MediaService } from "src/service/media.service";

@Component({
    selector: "app-popup-document-security",
    templateUrl: "./popup-document-security.component.html",
    styleUrls: ["./popup-document-security.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PopupDocumentSecurityComponent implements OnInit, OnDestroy {
  @Input() document: DocumentSecurity = null;

  public formDocument: UntypedFormGroup = null;
  public selectedFile: File = null;
  public isLoading: boolean = false;
  public isEditing: boolean = false;

  /**
   * Names offered in the type dropdown: the active catalog plus, when editing,
   * the document's current type even if it has since been (logically) removed
   * from the catalog, so the existing value is never silently lost.
   */
  public documentTypeOptions: Array<string> = [];
  /** Same list, in the shape of the select field */
  public documentTypeSelectOptions: Array<SelectFieldOption<string>> = [];

  public titleLabel: string = "";
  public newDocumentLabel: string = "";
  public typeLabel: string = "";
  public expirationLabel: string = "";
  public fileLabel: string = "";
  public currentFileLabel: string = "";
  public newFileLabel: string = "";
  public saveLabel: string = "";
  public cancelLabel: string = "";
  public replacementWarningTitle: string = "";
  public replacementWarningDescription: string = "";
  public fileRequiredLabel: string = "";

  private _translationSubscription: Subscription = null;

  constructor(
    private _modalController: ModalController,
    private _formBuilder: UntypedFormBuilder,
    private _documentSecurityService: DocumentSecurityService,
    private _alertService: AlertService,
    private _translateService: TranslateService,
    private _loaderService: LoaderService,
    private _mediaService: MediaService,
    private _platform: Platform,
  ) {}

  /**
   * Derive edit vs create mode from the bound document, build the form and
   * load the type catalog and translations.
   */
  ngOnInit(): void {
    this.isEditing = !!this.document?.id;
    this.initializeForm();
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.loadDocumentTypes();
  }

  /**
   * Load the active type catalog for the dropdown. Keeps the document's current
   * type as a selectable option even if it is no longer in the catalog (e.g.
   * it was logically deleted), so editing does not blank a valid value.
   */
  private async loadDocumentTypes(): Promise<void> {
    try {
      const types = await this._documentSecurityService.getDocumentTypes();
      const names = (types || []).map((type: DocumentSecurityType) => type.name);

      const current = this.document?.documentType;
      if (current && !names.includes(current)) {
        names.unshift(current);
      }

      this.documentTypeOptions = names;
    } catch {
      // On failure keep at least the current value so editing still works.
      const current = this.document?.documentType;
      this.documentTypeOptions = current ? [current] : [];
    }
    this.documentTypeSelectOptions = this.documentTypeOptions.map((name) => ({
      value: name,
      label: name,
    }));
  }

  /**
   * Unsubscribe from the language-change stream to avoid leaks.
   */
  ngOnDestroy(): void {
    this._translationSubscription?.unsubscribe();
  }

  /**
   * Track the file picked in the file input (or clear it when none is chosen).
   */
  public onFileChange(event: any): void {
    const files = (event.target as HTMLInputElement).files;
    this.selectedFile = files?.length > 0 ? files[0] : null;
  }

  /**
   * Close the modal without saving.
   */
  public onCancel(): void {
    this._modalController.dismiss({ saved: false });
  }

  /**
   * Download the file currently bound to the record being edited, so the user
   * can review the existing document before replacing it. Same flow used by the
   * list page: fetch the serialized buffer, rebuild the blob, then save it via
   * the platform-specific download handler.
   */
  public async onClickDownloadCurrent(): Promise<void> {
    if (!this.document?.id || !this.document?.filename) return;

    try {
      await this._loaderService.presentLoaderWaiting();
      const responseData = await this._documentSecurityService.getUploadedFile(
        this.document.id,
      );
      const blob: Blob = this._mediaService.jsonStringToBlob(responseData.data);

      if (this._platform.is("ios") || this._platform.is("android")) {
        await this._mediaService.handleMobileDownload(
          blob,
          this.document.filename,
        );
      } else {
        this._mediaService.handleDesktopBrowserDownload(
          this.document.filename,
          blob,
        );
      }
    } catch {
      await this._alertService.presentAlertSomethingWrong();
    } finally {
      await this._loaderService.dismissLoaderWaiting();
    }
  }

  /**
   * Validate and persist the document. On create a file is mandatory; on edit
   * the user must confirm the replacement warning first. Saves the metadata
   * (create or update), then uploads the file when one was selected, and
   * finally dismisses the modal signalling success.
   */
  public async onClickSave(): Promise<void> {
    if (this.formDocument.invalid) {
      this.formDocument.markAllAsTouched();
      return;
    }

    if (!this.isEditing && !this.selectedFile) {
      await this._alertService.presentAlertBillsQuotesCorrespondingMsg(
        false,
        "",
        this.fileRequiredLabel,
      );
      return;
    }

    if (this.isEditing) {
      const userConfirmed =
        await this._alertService.presentAlertConfirmOrDismiss(
          this.replacementWarningTitle,
          "",
          this.replacementWarningDescription,
        );
      if (!userConfirmed) return;
    }

    try {
      this.isLoading = true;
      await this._loaderService.presentLoaderWaiting();
      const payload = {
        documentType: this.formDocument.value.documentType,
        expirationDate: this.formDocument.value.expirationDate,
        filename:
          this.selectedFile?.name || this.formDocument.value.filename || "",
      };

      let savedDocument: DocumentSecurity;
      if (this.isEditing && this.document?.id) {
        savedDocument = await this._documentSecurityService.updateDocument(
          this.document.id,
          payload,
        );
      } else {
        savedDocument =
          await this._documentSecurityService.createDocument(payload);
      }

      if (this.selectedFile && savedDocument?.id) {
        await this.uploadSelectedFile(savedDocument);
      }

      this._modalController.dismiss({ saved: true });
    } catch {
      await this._alertService.presentAlertSomethingWrong();
    } finally {
      await this._loaderService.dismissLoaderWaiting();
      this.isLoading = false;
    }
  }

  /**
   * Upload the selected file straight to S3 via a backend-issued presigned PUT
   * URL, so no AWS credentials ever reach the client:
   *   1. ask the backend for a short-lived presigned URL for this record;
   *   2. PUT the raw file directly to S3 over HTTPS (native fetch, to bypass
   *      the auth interceptor that would otherwise poison the signed request);
   *   3. confirm the upload so the backend binds the file to the record.
   * On the create flow, if any step fails we roll back the freshly created
   * record so it does not stay around without its file.
   */
  private async uploadSelectedFile(document: DocumentSecurity): Promise<void> {
    try {
      const namefile = this.selectedFile.name;
      const uploadUrl = await this._documentSecurityService.getUploadUrl(
        document.id,
        namefile,
      );
      await this._documentSecurityService.uploadFileToPresignedUrl(
        uploadUrl,
        this.selectedFile,
      );
      await this._documentSecurityService.confirmUpload(document.id, namefile);
    } catch (uploadError) {
      if (!this.isEditing) {
        try {
          await this._documentSecurityService.deleteDocument(document.id);
        } catch {
          // ignore rollback failure: the original upload error is reported
        }
      }
      throw uploadError;
    }
  }

  /**
   * Build the reactive form, prefilling it from the bound document when editing.
   * Type and expiration date are required; filename is kept only to preserve the
   * existing name when no new file is uploaded.
   */
  private initializeForm(): void {
    this.formDocument = this._formBuilder.group({
      documentType: [
        this.document?.documentType || "",
        Validators.required,
      ],
      expirationDate: [
        this.toInputDate(this.document?.expirationDate),
        Validators.required,
      ],
      filename: [this.document?.filename || ""],
    });
  }

  /**
   * Normalize a stored date to the yyyy-MM-dd format expected by the native
   * date input, returning an empty string when there is no date.
   */
  private toInputDate(date: string): string {
    if (!date) return "";
    return new Date(date).toISOString().split("T")[0];
  }

  /**
   * Resolve all static labels for the active language; re-run on language change.
   */
  private loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.documentSecurity",
        "dictionary.newDocument",
        "dictionary.documentType",
        "dictionary.documentExpirationDate",
        "dictionary.documentFile",
        "dictionary.currentDocument",
        "dictionary.newFile",
        "dictionary.save",
        "dictionary.cancel",
        "dictionary.documentReplacementWarningTitle",
        "dictionary.documentReplacementWarningDescription",
        "dictionary.documentFileRequired",
      ])
      .subscribe((translation) => {
        this.titleLabel = translation["dictionary.documentSecurity"];
        this.newDocumentLabel = translation["dictionary.newDocument"];
        this.typeLabel = translation["dictionary.documentType"];
        this.expirationLabel = translation["dictionary.documentExpirationDate"];
        this.fileLabel = translation["dictionary.documentFile"];
        this.currentFileLabel = translation["dictionary.currentDocument"];
        this.newFileLabel = translation["dictionary.newFile"];
        this.saveLabel = translation["dictionary.save"];
        this.cancelLabel = translation["dictionary.cancel"];
        this.replacementWarningTitle =
          translation["dictionary.documentReplacementWarningTitle"];
        this.replacementWarningDescription =
          translation["dictionary.documentReplacementWarningDescription"];
        this.fileRequiredLabel = translation["dictionary.documentFileRequired"];
      });
  }

  /**
   * Re-resolve the labels whenever the app language changes at runtime.
   */
  private subscribeTranslationEvent(): void {
    this._translationSubscription =
      this._translateService.onLangChange.subscribe(() => {
        this.loadTranslation();
      });
  }
}
